import React, { useState } from 'react';
import './ContactPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { contactService } from '../services/contactService';
import useSEO from '../hooks/useSEO';

function ContactPage() {
  useSEO({
    title: "Contact & Localisation | Togba, Abomey-Calavi (Bénin)",
    description: "Contactez le Groupe Scolaire et Universitaire Les Palmiers à Togba (Abomey-Calavi, Bénin). Formulaire de message direct, secrétariat, admissions et plan d'accès.",
    keywords: "contact GSU Les Palmiers, adresse école Togba, téléphone lycée Les Palmiers, localisation Abomey-Calavi Bénin, secrétariat école Palmiers",
    canonicalUrl: "https://gsulespalmiers.bj/contact"
  });

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await contactService.submit(formData);
      setSuccess(true);
      // Réinitialiser le formulaire
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        sujet: '',
        message: ''
      });
      
      // Masquer le message de succès après 5 secondes
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer.';
      setError(errorMessage);
      console.error('Erreur lors de l\'envoi du message:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <Header activePage="contact" />
      
      <section className="contact-hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Contactez-nous</h1>
            <p className="hero-description">
              Une question, une idée ou un besoin ? Notre équipe est toujours disponible 
              pour vous répondre.
            </p>
          </div>
        </div>
      </section>

      <section className="contact-main section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-image-section">
              <img 
                src="/images/parent-eleve-contact.png" 
                alt="Parent d'élève et élève - GSU Les Palmiers" 
                className="contact-image"
              />
              <div className="gsu-badge-overlay">
                <img src="/images/logo-gsu.png" alt="Logo GSU" className="gsu-badge-logo" />
                <span>GSU Les Palmiers</span>
              </div>
            </div>

            <div className="form-section">
              <h2>Contactez-nous</h2>
              <p className="form-subtitle">
                Une question ? Nous sommes là pour vous accompagner.
              </p>

              {success && (
                <div className="alert alert-success">
                  Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.
                </div>
              )}

              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <input
                    type="text"
                    name="nom"
                    placeholder="Nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <input
                    type="text"
                    name="prenom"
                    placeholder="Prénom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>

                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />

                <input
                  type="tel"
                  name="telephone"
                  placeholder="Téléphone"
                  value={formData.telephone}
                  onChange={handleChange}
                  disabled={loading}
                />

                <input
                  type="text"
                  name="sujet"
                  placeholder="Sujet"
                  value={formData.sujet}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />

                <textarea
                  name="message"
                  placeholder="Message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={loading}
                ></textarea>

                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'Envoi en cours...' : 'Envoyer le message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-info-section">
        <div className="container">
          <div className="contact-info-header">
            <span className="contact-info-tag">Informations pratiques</span>
            <h2>Comment nous trouver & nous joindre</h2>
            <p>Notre équipe administrative est à votre disposition pour toute information ou visite sur notre campus.</p>
          </div>

          <div className="contact-info-grid">
            <div className="info-card">
              <div className="info-card-top">
                <div className="info-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <span className="card-badge">Localisation</span>
              </div>
              <h3>Adresse du Lycée</h3>
              <p className="info-desc">Togba, Abomey-Calavi<br />Bénin</p>
              <a 
                href="https://maps.google.com/?q=Togba+Abomey-Calavi+Benin" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="info-action-btn"
              >
                <span>Voir sur la carte</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
            </div>

            <div className="info-card">
              <div className="info-card-top">
                <div className="info-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <span className="card-badge">Téléphone</span>
              </div>
              <h3>Ligne Directe</h3>
              <div className="info-links">
                <a href="tel:+22997305230" className="info-link">+229 97 30 52 30</a>
                <a href="tel:+2290199649934" className="info-link">01 99 64 99 34</a>
                <a href="tel:+2290148258320" className="info-link">01 48 25 83 20</a>
                <a href="tel:+2290162003980" className="info-link">01 62 00 39 80</a>
              </div>
              <a href="tel:+22997305230" className="info-action-btn">
                <span>Appeler maintenant</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </a>
            </div>

            <div className="info-card">
              <div className="info-card-top">
                <div className="info-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <span className="card-badge">Email</span>
              </div>
              <h3>Courrier Électronique</h3>
              <div className="info-links">
                <a href="mailto:contact@gsulespalmiers.bj" className="info-link">contact@gsulespalmiers.bj</a>
                <a href="mailto:info@gsulespalmiers.bj" className="info-link">info@gsulespalmiers.bj</a>
              </div>
              <a href="mailto:contact@gsulespalmiers.bj" className="info-action-btn">
                <span>Envoyer un mail</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </a>
            </div>

            <div className="info-card">
              <div className="info-card-top">
                <div className="info-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12,6 12,12 16,14"></polyline>
                  </svg>
                </div>
                <span className="card-badge status-open">🟢 Ouvert</span>
              </div>
              <h3>Horaires d'ouverture</h3>
              <div className="info-schedule">
                <div className="schedule-row">
                  <span>Lun - Ven :</span>
                  <strong>7h30 - 17h30</strong>
                </div>
                <div className="schedule-row">
                  <span>Samedi :</span>
                  <strong>8h00 - 12h00</strong>
                </div>
              </div>
              <div className="info-action-btn disabled">
                <span>Accueil Secrétariat</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ContactPage;
